import type {
  Answer,
  AnswerAccepted,
  App,
  AppConfiguration,
  AppDeletion,
  Asset,
  ClientBundle,
  ConfigurationPatch,
  ConnectionAssign,
  ConnectionListing,
  Definition,
  Draft,
  DraftAppMutation,
  DraftDeletion,
  DraftDeployment,
  DraftMutation,
  DraftReview,
  DraftRevisionPage,
  DraftTemplateList,
  DraftValidation,
  JsonObject,
  KnowledgeContentReceipt,
  KnowledgeDeletion,
  KnowledgeFileDeclaration,
  KnowledgeFinalizeResult,
  KnowledgeItem,
  KnowledgeItemCategory,
  KnowledgeItemDetail,
  KnowledgeItemPage,
  KnowledgeKindList,
  KnowledgeSearchResult,
  KnowledgeSource,
  KnowledgeSourceCreateInput,
  KnowledgeSourceList,
  KnowledgeSourcePatchInput,
  KnowledgeTestResult,
  KnowledgeUpload,
  KnowledgeUploadList,
  Memory,
  MemoryKind,
  MemoryPage,
  MessageList,
  MessageStart,
  ModelKey,
  Page,
  Problem,
  ProposalDecision,
  ProposalList,
  ProposalStatus,
  PublicConfiguration,
  Publication,
  PublicationPage,
  ReviewReport,
  ReviewReportFilterKind,
  ReviewReportPage,
  RevisionDiff,
  RpcInput,
  RpcOutput,
  Run,
  RunDiagram,
  RunStart,
  RunStatus,
  SessionControl,
  SessionDetail,
  SessionStart,
  SourceBundle,
  StudioAgent,
  StudioAgentList,
  StudioSessionPage,
  StudioTarget,
  StudioTrial,
  StudioTrialPage,
  TaskRequest,
  TrialStart,
  TurnAccepted,
  Upload,
  Workspace,
  WorkspaceCreate,
  WorkspaceDeletion,
} from './types';
import type { Transport, TransportRequest, TransportResponse } from './transport';

/** サーバーが返した application/problem+json を保持する失敗。 */
export class ApiError extends Error {
  constructor(
    readonly problem: Problem,
    readonly requestPath: string,
  ) {
    super(`${problem.status} ${problem.code}: ${problem.title}`);
    this.name = 'ApiError';
  }

  get status(): number {
    return this.problem.status;
  }
}

export interface PageQuery {
  limit?: number;
  cursor?: string;
}

export interface AssetContent {
  bytes: Uint8Array;
  mediaType: string;
}

/** Draft のファイル本文。sha256 は ETag の引用符を外したもの（ETag が無ければ null）。 */
export interface DraftFileContent {
  bytes: Uint8Array;
  mediaType: string;
  sha256: string | null;
}

/**
 * 書き出した Draft の ZIP。fileName は Content-Disposition の filename*（無ければ draft.zip）。
 * 別 origin の配備でこのヘッダを読めるのは、API が CORS で公開しているから（S2-T9）。
 */
export interface DraftArchive {
  bytes: Uint8Array;
  fileName: string;
}

/** 質問への回答 1 問ぶん。choices は QuestionOption.id（C19）。 */
export interface QuestionAnswerInput {
  n: number;
  choices?: string[];
  text?: string;
  defer?: boolean;
}

/** セッションの作成・ターンの投入の本文。attachments は知識ソースの項目 id。 */
export interface TurnInput {
  text: string;
  target?: StudioTarget;
  attachments?: string[];
}

/**
 * 稼働中パッケージのファイル一式を、画面が引きやすい形へ畳んだもの。
 *
 * wireの [path, 内容] 配列は順序も情報なので paths として残し、内容の引きは
 * Map にする。1リクエストで全部届くので、ファイル切替で再取得しない。
 */
export interface AppSource {
  uploadId: string;
  digest: string;
  paths: string[];
  contents: Map<string, string>;
  omitted: SourceBundle['omitted'];
}

export interface ClientBundleResponse {
  bundle: ClientBundle;
  /** RPC/run開始/回答のIf-Matchに使う現在artifactのdigest。 */
  etag: string | null;
}

/** 冪等キーは「同じ論理操作の再送」を表すため、UI側で操作単位に1つ作って再利用する。 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `idem_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export class ApiClient {
  constructor(private readonly transport: Transport) {}

  // ---- Workspace -------------------------------------------------------

  listWorkspaces(query: PageQuery & { q?: string } = {}): Promise<Page<Workspace>> {
    return this.json({ method: 'GET', path: '/v1/workspaces', params: { ...query } });
  }

  createWorkspace(body: WorkspaceCreate, idempotencyKey: string): Promise<Workspace> {
    return this.json({
      method: 'POST',
      path: '/v1/workspaces',
      json: body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  getWorkspace(workspaceId: string): Promise<Workspace> {
    return this.json({ method: 'GET', path: `/v1/workspaces/${enc(workspaceId)}` });
  }

  /**
   * Workspaceを中のmicroapp・業務データごと抹消する。取り消せない。
   * 中断後や応答を受け取れなかった後の再試行は同じidempotencyKeyで送る。
   * 削除が済んでいれば404ではなく最初の応答が返る。
   */
  deleteWorkspace(
    workspaceId: string,
    options: { idempotencyKey: string },
  ): Promise<WorkspaceDeletion> {
    return this.json({
      method: 'DELETE',
      path: `/v1/workspaces/${enc(workspaceId)}`,
      headers: { 'Idempotency-Key': options.idempotencyKey },
    });
  }

  // ---- microapp --------------------------------------------------------

  listApps(workspaceId: string, query: PageQuery = {}): Promise<Page<App>> {
    return this.json({
      method: 'GET',
      path: `/v1/workspaces/${enc(workspaceId)}/microapps`,
      params: { ...query },
    });
  }

  uploadApp(
    workspaceId: string,
    bundle: Blob,
    options: { fileName: string; idempotencyKey: string },
  ): Promise<Upload> {
    // 設定値はZIPと別に設定APIで入れる。実APIはbundle以外のpartを受けない。
    const form = new FormData();
    form.append('bundle', bundle, options.fileName);
    return this.json({
      method: 'POST',
      path: `/v1/workspaces/${enc(workspaceId)}/microapps:upload`,
      form,
      headers: { 'Idempotency-Key': options.idempotencyKey },
    });
  }

  getUpload(workspaceId: string, uploadId: string): Promise<Upload> {
    return this.json({
      method: 'GET',
      path: `/v1/workspaces/${enc(workspaceId)}/uploads/${enc(uploadId)}`,
    });
  }

  getApp(workspaceId: string, appId: string): Promise<App> {
    return this.json({ method: 'GET', path: appPath(workspaceId, appId) });
  }

  /**
   * microappを1つ抹消する。取り消せない。同じWorkspaceの他のappは残る。
   * engineへ届かず中断した場合（503）はappがupdatingのまま残るので、
   * 同じidempotencyKeyで再試行すれば続きから終わる。
   */
  deleteApp(
    workspaceId: string,
    appId: string,
    options: { idempotencyKey: string },
  ): Promise<AppDeletion> {
    return this.json({
      method: 'DELETE',
      path: appPath(workspaceId, appId),
      headers: { 'Idempotency-Key': options.idempotencyKey },
    });
  }

  getDefinition(workspaceId: string, appId: string): Promise<Definition> {
    return this.json({ method: 'GET', path: `${appPath(workspaceId, appId)}/definition` });
  }

  async getClient(workspaceId: string, appId: string): Promise<ClientBundleResponse> {
    const response = await this.send({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/client`,
    });
    const bundle = await this.decode<ClientBundle>(response, `${appPath(workspaceId, appId)}/client`);
    return { bundle, etag: response.header('ETag') };
  }

  async getSource(workspaceId: string, appId: string): Promise<AppSource> {
    const bundle = await this.json<SourceBundle>({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/source`,
    });
    return {
      uploadId: bundle.uploadId,
      digest: bundle.digest,
      paths: bundle.files.map(([path]) => path),
      contents: new Map(bundle.files),
      omitted: bundle.omitted,
    };
  }

  listAssets(workspaceId: string, appId: string, query: PageQuery = {}): Promise<Page<Asset>> {
    return this.json({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/assets`,
      params: { ...query },
    });
  }

  async getAssetContent(
    workspaceId: string,
    appId: string,
    assetId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<AssetContent> {
    const path = `${appPath(workspaceId, appId)}/assets/${enc(assetId)}/content`;
    const response = await this.send({ method: 'GET', path, signal: options.signal });
    if (response.status >= 400) throw await this.toError(response, path);
    return {
      bytes: await response.bytes(),
      mediaType: response.header('Content-Type') ?? 'application/octet-stream',
    };
  }

  callRpc(
    workspaceId: string,
    appId: string,
    body: RpcInput,
    options: { ifMatch?: string; signal?: AbortSignal } = {},
  ): Promise<RpcOutput> {
    return this.json({
      method: 'POST',
      path: `${appPath(workspaceId, appId)}/rpc`,
      json: body,
      headers: ifMatchHeader(options.ifMatch),
      signal: options.signal,
    });
  }

  // ---- run / task ------------------------------------------------------

  startRun(
    workspaceId: string,
    appId: string,
    body: RunStart,
    options: { idempotencyKey: string; ifMatch?: string; signal?: AbortSignal },
  ): Promise<Run> {
    return this.json({
      method: 'POST',
      path: `${appPath(workspaceId, appId)}/runs`,
      json: body,
      headers: { 'Idempotency-Key': options.idempotencyKey, ...ifMatchHeader(options.ifMatch) },
      signal: options.signal,
    });
  }

  listRuns(
    workspaceId: string,
    appId: string,
    query: PageQuery & { status?: RunStatus } = {},
  ): Promise<Page<Run>> {
    return this.json({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/runs`,
      params: { ...query },
    });
  }

  startByMessage(
    workspaceId: string,
    appId: string,
    processKey: string,
    body: MessageStart,
    options: { idempotencyKey: string; signal?: AbortSignal },
  ): Promise<Run> {
    return this.json({
      method: 'POST',
      path: `${appPath(workspaceId, appId)}/processes/${enc(processKey)}/message-starts`,
      json: body,
      headers: { 'Idempotency-Key': options.idempotencyKey },
      signal: options.signal,
    });
  }

  getRun(workspaceId: string, runId: string, options: { signal?: AbortSignal } = {}): Promise<Run> {
    return this.json({
      method: 'GET',
      path: `/v1/workspaces/${enc(workspaceId)}/runs/${enc(runId)}`,
      signal: options.signal,
    });
  }

  getRunDiagram(
    workspaceId: string,
    runId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<RunDiagram> {
    return this.json({
      method: 'GET',
      path: `/v1/workspaces/${enc(workspaceId)}/runs/${enc(runId)}/diagram`,
      signal: options.signal,
    });
  }

  getTask(
    workspaceId: string,
    requestId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<TaskRequest> {
    return this.json({
      method: 'GET',
      path: `/v1/workspaces/${enc(workspaceId)}/task-requests/${enc(requestId)}`,
      signal: options.signal,
    });
  }

  answerTask(
    workspaceId: string,
    requestId: string,
    body: Answer,
    options: { idempotencyKey: string; ifMatch?: string; signal?: AbortSignal },
  ): Promise<TaskRequest> {
    return this.json({
      method: 'POST',
      path: `/v1/workspaces/${enc(workspaceId)}/task-requests/${enc(requestId)}/answers`,
      json: body,
      headers: { 'Idempotency-Key': options.idempotencyKey, ...ifMatchHeader(options.ifMatch) },
      signal: options.signal,
    });
  }

  // ---- configuration ---------------------------------------------------

  getAppConfiguration(workspaceId: string, appId: string): Promise<AppConfiguration> {
    return this.json({ method: 'GET', path: `${appPath(workspaceId, appId)}/config` });
  }

  updateAppConfiguration(
    workspaceId: string,
    appId: string,
    body: ConfigurationPatch,
    options: { idempotencyKey: string },
  ): Promise<AppConfiguration> {
    return this.json({
      method: 'PATCH',
      path: `${appPath(workspaceId, appId)}/config`,
      json: body,
      headers: { 'Idempotency-Key': options.idempotencyKey },
    });
  }

  // ---- connections -----------------------------------------------------

  listConnections(workspaceId: string, appId: string): Promise<ConnectionListing> {
    return this.json({ method: 'GET', path: `${appPath(workspaceId, appId)}/connections` });
  }

  /** 宣言された名前に provider の設定（受け方・token・対象・許可）を入れる。省略・空の token は変えない。 */
  assignConnection(
    workspaceId: string,
    appId: string,
    name: string,
    body: ConnectionAssign,
    options: { idempotencyKey: string },
  ): Promise<ConnectionListing> {
    return this.json({
      method: 'PUT',
      path: `${appPath(workspaceId, appId)}/connections/${enc(name)}`,
      json: body,
      headers: { 'Idempotency-Key': options.idempotencyKey },
    });
  }

  /** 接続先の設定を解除する。token も消え、この接続先の購読は disabled になる。 */
  unassignConnection(
    workspaceId: string,
    appId: string,
    name: string,
    options: { idempotencyKey: string },
  ): Promise<ConnectionListing> {
    return this.json({
      method: 'DELETE',
      path: `${appPath(workspaceId, appId)}/connections/${enc(name)}`,
      headers: { 'Idempotency-Key': options.idempotencyKey },
    });
  }

  getPublicAppConfiguration(
    workspaceId: string,
    appId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<PublicConfiguration> {
    return this.json({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/config/public`,
      signal: options.signal,
    });
  }

  // ---- ナレッジ（studio） ----------------------------------------------

  listKnowledgeSources(workspaceId: string): Promise<KnowledgeSourceList> {
    return this.json({ method: 'GET', path: `${workspacePath(workspaceId)}/knowledge-sources` });
  }

  /** 追加できる種類（組み込みの studio / file は含まない）。接続フォームはこの settingsSchema から描く。 */
  listKnowledgeKinds(workspaceId: string): Promise<KnowledgeKindList> {
    return this.json({ method: 'GET', path: `${workspacePath(workspaceId)}/knowledge-sources/kinds` });
  }

  /** 種類ごとに 1 つ（2 つ目は 409 source_exists）。設定の誤りは 422 invalid_settings。 */
  createKnowledgeSource(
    workspaceId: string,
    body: KnowledgeSourceCreateInput,
    idempotencyKey: string,
  ): Promise<KnowledgeSource> {
    return this.json({
      method: 'POST',
      path: `${workspacePath(workspaceId)}/knowledge-sources`,
      json: body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  /** expectedRevision の CAS（409 revision_conflict）。本文に書いた項目だけを変える。 */
  updateKnowledgeSource(
    workspaceId: string,
    sourceId: string,
    body: KnowledgeSourcePatchInput,
    idempotencyKey: string,
  ): Promise<KnowledgeSource> {
    return this.json({
      method: 'PATCH',
      path: `${workspacePath(workspaceId)}/knowledge-sources/${enc(sourceId)}`,
      json: body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  deleteKnowledgeSource(
    workspaceId: string,
    sourceId: string,
    idempotencyKey: string,
  ): Promise<KnowledgeDeletion> {
    return this.json({
      method: 'DELETE',
      path: `${workspacePath(workspaceId)}/knowledge-sources/${enc(sourceId)}`,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  /** 接続確認。本文は `{}`。状態を変えないので冪等キーは付けない。 */
  testKnowledgeSource(workspaceId: string, sourceId: string): Promise<KnowledgeTestResult> {
    return this.json({
      method: 'POST',
      path: `${workspacePath(workspaceId)}/knowledge-sources/${enc(sourceId)}:test`,
      json: {},
    });
  }

  listKnowledgeItems(
    workspaceId: string,
    query: PageQuery & { sourceId?: string; category?: KnowledgeItemCategory; q?: string } = {},
  ): Promise<KnowledgeItemPage> {
    return this.json({
      method: 'GET',
      path: `${workspacePath(workspaceId)}/knowledge/items`,
      params: { ...query },
    });
  }

  initializeKnowledgeFiles(
    workspaceId: string,
    files: KnowledgeFileDeclaration[],
    idempotencyKey: string,
  ): Promise<KnowledgeUploadList> {
    return this.json({
      method: 'POST',
      path: `${workspacePath(workspaceId)}/knowledge/files:initialize`,
      json: { files },
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  putKnowledgeFileContent(
    upload: KnowledgeUpload,
    file: Blob,
    idempotencyKey: string,
  ): Promise<KnowledgeContentReceipt> {
    // S1 は API の PUT だけ。別オリジンの署名付きURLへ transport の Bearer を付けて送ってはいけないので扱わない。
    if (!upload.url.startsWith('/v1/')) {
      return Promise.reject(new Error('署名付きURLへのアップロードにはまだ対応していません'));
    }
    return this.json({
      method: 'PUT',
      path: upload.url,
      body: file,
      contentType: 'application/octet-stream',
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  finalizeKnowledgeFiles(
    workspaceId: string,
    files: Array<{ fileId: string; key: string }>,
    idempotencyKey: string,
  ): Promise<KnowledgeFinalizeResult> {
    return this.json({
      method: 'POST',
      path: `${workspacePath(workspaceId)}/knowledge/files:finalize`,
      json: { files },
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  /**
   * 3段階（受付 → 本体 → 確定）をまとめて行う。応答の uploads は宣言と同じ順に並ぶ。
   * 途中で失敗したら投げる。確定しなかった項目は受付中か失敗として一覧に残り、同じ名前で追加し直せる。
   * 確定の invalids は fileId しか持たないので、名前を引けるよう受付の一覧も一緒に返す。
   */
  async uploadKnowledgeFiles(
    workspaceId: string,
    files: File[],
  ): Promise<{ result: KnowledgeFinalizeResult; uploads: KnowledgeUpload[] }> {
    const { uploads } = await this.initializeKnowledgeFiles(
      workspaceId,
      files.map((file) => ({ name: file.name, size: file.size, mediaType: file.type })),
      newIdempotencyKey(),
    );
    for (const [index, upload] of uploads.entries()) {
      const file = files[index];
      if (!file) throw new Error(`${upload.name} の本体が見つかりません`);
      await this.putKnowledgeFileContent(upload, file, newIdempotencyKey());
    }
    const result = await this.finalizeKnowledgeFiles(
      workspaceId,
      uploads.map(({ fileId, key }) => ({ fileId, key })),
      newIdempotencyKey(),
    );
    return { result, uploads };
  }

  async getKnowledgeOriginal(
    workspaceId: string,
    itemId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<AssetContent> {
    const path = `${workspacePath(workspaceId)}/knowledge/items/${enc(itemId)}/original`;
    const response = await this.send({ method: 'GET', path, signal: options.signal });
    if (response.status >= 400) throw await this.toError(response, path);
    return {
      bytes: await response.bytes(),
      mediaType: response.header('Content-Type') ?? 'application/octet-stream',
    };
  }

  deleteKnowledgeItem(workspaceId: string, itemId: string, idempotencyKey: string): Promise<KnowledgeDeletion> {
    return this.json({
      method: 'DELETE',
      path: `${workspacePath(workspaceId)}/knowledge/items/${enc(itemId)}`,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  searchKnowledge(
    workspaceId: string,
    q: string,
    options: { sourceId?: string } = {},
  ): Promise<KnowledgeSearchResult> {
    return this.json({
      method: 'GET',
      path: `${workspacePath(workspaceId)}/knowledge:search`,
      params: { q, sourceId: options.sourceId },
    });
  }

  // ---- Draft（studio） ------------------------------------------------

  /** Draft が無ければ 404 draft_not_found。 */
  getDraft(workspaceId: string, appId: string): Promise<Draft> {
    return this.json({ method: 'GET', path: `${appPath(workspaceId, appId)}/draft` });
  }

  /** 既存の microapp の現行パッケージから Draft を起こす（計画の調整 B1）。 */
  createDraft(workspaceId: string, appId: string, idempotencyKey: string): Promise<DraftMutation> {
    return this.json({
      method: 'POST',
      path: `${appPath(workspaceId, appId)}/draft`,
      json: {},
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  async getDraftFile(
    workspaceId: string,
    appId: string,
    path: string,
    options: { revisionId?: string; signal?: AbortSignal } = {},
  ): Promise<DraftFileContent> {
    const url = draftFilePath(workspaceId, appId, path);
    const response = await this.send({
      method: 'GET',
      path: url,
      ...(options.revisionId ? { params: { revisionId: options.revisionId } } : {}),
      signal: options.signal,
    });
    if (response.status >= 400) throw await this.toError(response, url);
    return {
      bytes: await response.bytes(),
      mediaType: response.header('Content-Type') ?? 'application/octet-stream',
      sha256: unquoteEtag(response.header('ETag')),
    };
  }

  /**
   * 利用者の直接編集。If-Match は編集の前提にした head の Revision id で、進んでいれば 409 revision_conflict（B5）。
   * 本文は JSON にせず octet-stream で送る（B6）。ブラウザの octet-stream は Idempotency-Key が要る。
   */
  putDraftFile(
    workspaceId: string,
    appId: string,
    path: string,
    content: string,
    options: { headRevisionId: string; idempotencyKey: string },
  ): Promise<DraftMutation> {
    return this.json({
      method: 'PUT',
      path: draftFilePath(workspaceId, appId, path),
      body: new Blob([content]),
      contentType: 'application/octet-stream',
      headers: {
        'If-Match': `"${options.headRevisionId}"`,
        'Idempotency-Key': options.idempotencyKey,
      },
    });
  }

  deleteDraftFile(
    workspaceId: string,
    appId: string,
    path: string,
    options: { headRevisionId: string; idempotencyKey: string },
  ): Promise<DraftMutation> {
    return this.json({
      method: 'DELETE',
      path: draftFilePath(workspaceId, appId, path),
      headers: {
        'If-Match': `"${options.headRevisionId}"`,
        'Idempotency-Key': options.idempotencyKey,
      },
    });
  }

  /** 版の履歴（新しい順）。 */
  listDraftRevisions(workspaceId: string, appId: string, query: PageQuery = {}): Promise<DraftRevisionPage> {
    return this.json({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/draft/revisions`,
      params: { ...query },
    });
  }

  /** fromRevisionId が前、to（省略時は head）が後。view の視点だけが埋まって返る（B11）。 */
  getRevisionDiff(
    workspaceId: string,
    appId: string,
    fromRevisionId: string,
    options: { to?: string; view: 'file' | 'element' | 'source' },
  ): Promise<RevisionDiff> {
    return this.json({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/draft/revisions/${enc(fromRevisionId)}/diff`,
      params: { view: options.view, ...(options.to ? { to: options.to } : {}) },
    });
  }

  /** その版のファイル表で新しい版を作る。head と同じなら版を作らず 200。 */
  restoreDraftRevision(
    workspaceId: string,
    appId: string,
    revisionId: string,
    idempotencyKey: string,
  ): Promise<DraftMutation> {
    return this.json({
      method: 'POST',
      path: `${appPath(workspaceId, appId)}/draft/revisions/${enc(revisionId)}:restore`,
      json: {},
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  async exportDraft(
    workspaceId: string,
    appId: string,
    options: { revisionId?: string } = {},
  ): Promise<DraftArchive> {
    const url = `${appPath(workspaceId, appId)}/draft:export`;
    const response = await this.send({
      method: 'GET',
      path: url,
      ...(options.revisionId ? { params: { revisionId: options.revisionId } } : {}),
    });
    if (response.status >= 400) throw await this.toError(response, url);
    return {
      bytes: await response.bytes(),
      fileName: attachmentFileName(response.header('Content-Disposition')) ?? 'draft.zip',
    };
  }

  // ---- Studio（Agent・会話・変更案・記憶） -------------------------------

  listAgents(workspaceId: string): Promise<StudioAgentList> {
    return this.json({ method: 'GET', path: `${workspacePath(workspaceId)}/agents` });
  }

  /** 版の CAS だけで、冪等キーは取らない（C37）。 */
  updateAgent(
    workspaceId: string,
    agentId: string,
    body: {
      expectedRevision: number;
      instructions?: string;
      model?: ModelKey | null;
      knowledgeSourceIds?: string[] | null;
      toolPolicy?: Record<string, unknown>;
    },
  ): Promise<StudioAgent> {
    return this.json({ method: 'PATCH', path: `${workspacePath(workspaceId)}/agents/${enc(agentId)}`, json: body });
  }

  /** 新しい順。 */
  listSessions(workspaceId: string, query: PageQuery = {}): Promise<StudioSessionPage> {
    return this.json({ method: 'GET', path: `${workspacePath(workspaceId)}/sessions`, params: { ...query } });
  }

  /** セッションを作り、最初のターンを積む。進行中のセッションがあれば 409 session_busy。 */
  startSession(
    workspaceId: string,
    body: TurnInput & { agentId?: string },
    idempotencyKey: string,
  ): Promise<SessionStart> {
    return this.json({
      method: 'POST',
      path: `${workspacePath(workspaceId)}/sessions`,
      json: body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  getSession(workspaceId: string, sessionId: string): Promise<SessionDetail> {
    return this.json({ method: 'GET', path: sessionPath(workspaceId, sessionId) });
  }

  /** after より大きい seq を昇順で返す（C42）。 */
  listSessionMessages(
    workspaceId: string,
    sessionId: string,
    query: { after?: number; limit?: number } = {},
  ): Promise<MessageList> {
    return this.json({
      method: 'GET',
      path: `${sessionPath(workspaceId, sessionId)}/messages`,
      params: { ...query },
    });
  }

  /** 回答待ちの間に送ると質問は superseded になる（C8）。作業中は 429 turn_limit。 */
  postTurn(workspaceId: string, sessionId: string, body: TurnInput, idempotencyKey: string): Promise<TurnAccepted> {
    return this.json({
      method: 'POST',
      path: `${sessionPath(workspaceId, sessionId)}/turns`,
      json: body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  answerQuestion(
    workspaceId: string,
    sessionId: string,
    questionId: string,
    answers: QuestionAnswerInput[],
    idempotencyKey: string,
  ): Promise<AnswerAccepted> {
    return this.json({
      method: 'POST',
      path: `${sessionPath(workspaceId, sessionId)}/questions/${enc(questionId)}/answers`,
      json: { answers },
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  cancelSession(workspaceId: string, sessionId: string, idempotencyKey: string): Promise<SessionControl> {
    return this.json({
      method: 'POST',
      path: `${sessionPath(workspaceId, sessionId)}:cancel`,
      json: {},
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  resumeSession(workspaceId: string, sessionId: string, idempotencyKey: string): Promise<SessionControl> {
    return this.json({
      method: 'POST',
      path: `${sessionPath(workspaceId, sessionId)}:resume`,
      json: {},
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  /** status はカンマで連結して送る。指定の無い条件は送らない。 */
  listProposals(
    workspaceId: string,
    query: { sessionId?: string; appId?: string; status?: ProposalStatus[] } = {},
  ): Promise<ProposalList> {
    return this.json({
      method: 'GET',
      path: `${workspacePath(workspaceId)}/proposals`,
      params: {
        ...(query.sessionId ? { sessionId: query.sessionId } : {}),
        ...(query.appId ? { appId: query.appId } : {}),
        ...(query.status && query.status.length > 0 ? { status: query.status.join(',') } : {}),
      },
    });
  }

  /** head が base から進んでいれば 409 revision_conflict（本文に headRevisionId と baseRevisionId）。 */
  acceptProposal(workspaceId: string, proposalId: string, idempotencyKey: string): Promise<ProposalDecision> {
    return this.json({
      method: 'POST',
      path: `${workspacePath(workspaceId)}/proposals/${enc(proposalId)}:accept`,
      json: {},
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  discardProposal(workspaceId: string, proposalId: string, idempotencyKey: string): Promise<ProposalDecision> {
    return this.json({
      method: 'POST',
      path: `${workspacePath(workspaceId)}/proposals/${enc(proposalId)}:discard`,
      json: {},
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  listMemories(
    workspaceId: string,
    query: { status?: 'active' | 'retired'; kind?: MemoryKind; limit?: number; cursor?: string } = {},
  ): Promise<MemoryPage> {
    return this.json({ method: 'GET', path: `${workspacePath(workspaceId)}/memories`, params: { ...query } });
  }

  createMemory(workspaceId: string, body: { text: string; kind: MemoryKind }, idempotencyKey: string): Promise<Memory> {
    return this.json({
      method: 'POST',
      path: `${workspacePath(workspaceId)}/memories`,
      json: body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  /** 訂正・退役・復帰。版の CAS だけで、冪等キーは取らない（C37）。 */
  updateMemory(
    workspaceId: string,
    memoryId: string,
    body: { expectedVersion: number; text?: string; kind?: MemoryKind; status?: 'active' | 'retired' },
  ): Promise<Memory> {
    return this.json({ method: 'PATCH', path: `${workspacePath(workspaceId)}/memories/${enc(memoryId)}`, json: body });
  }

  /** 参照している要素があれば 409 item_referenced。 */
  deleteMemory(workspaceId: string, memoryId: string, idempotencyKey: string): Promise<{ id: string; deleted: boolean }> {
    return this.json({
      method: 'DELETE',
      path: `${workspacePath(workspaceId)}/memories/${enc(memoryId)}`,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  getKnowledgeItem(workspaceId: string, itemId: string): Promise<KnowledgeItemDetail> {
    return this.json({ method: 'GET', path: `${workspacePath(workspaceId)}/knowledge/items/${enc(itemId)}` });
  }

  /** 会話まとめの編集。If-Match は編集を始めた時点の版（C22）。 */
  updateConversationSummary(
    workspaceId: string,
    itemId: string,
    markdown: string,
    version: number,
  ): Promise<KnowledgeItem> {
    return this.json({
      method: 'PATCH',
      path: `${workspacePath(workspaceId)}/knowledge/items/${enc(itemId)}`,
      json: { markdown },
      headers: { 'If-Match': `"${version}"` },
    });
  }

  /** 先頭は blank（C46）。 */
  listDraftTemplates(workspaceId: string): Promise<DraftTemplateList> {
    return this.json({ method: 'GET', path: `${workspacePath(workspaceId)}/studio/templates` });
  }

  /** Draft だけの microapp を作る（C10）。UI は blank と template だけ。appKey を省くと基盤が名前から作る（U1）。 */
  createDraftMicroapp(
    workspaceId: string,
    body: { name: string; appKey?: string; source: { kind: 'blank' | 'template'; ref?: string } },
    idempotencyKey: string,
  ): Promise<DraftAppMutation> {
    return this.json({
      method: 'POST',
      path: `${workspacePath(workspaceId)}/microapps:draft`,
      json: body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  /** Draft を捨てる。Draft だけの microapp なら app ごと消える（C34）。 */
  deleteDraft(workspaceId: string, appId: string, idempotencyKey: string): Promise<DraftDeletion> {
    return this.json({
      method: 'DELETE',
      path: `${appPath(workspaceId, appId)}/draft`,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  // ---- レビューと段階（studio。計画の S4） ------------------------------

  /** head の検証の状態・次へ進める条件・「試す」を止めている理由。Draft が無ければ 404 draft_not_found。 */
  getDraftReview(workspaceId: string, appId: string): Promise<DraftReview> {
    return this.json({ method: 'GET', path: `${appPath(workspaceId, appId)}/draft/review` });
  }

  /** head を同期で検証する（build はしない。D11）。再実行して害が無いので冪等キーを付けない。 */
  validateDraft(workspaceId: string, appId: string): Promise<DraftValidation> {
    return this.json({ method: 'POST', path: `${appPath(workspaceId, appId)}/draft:validate`, json: {} });
  }

  /** 新しい順。一覧には kind=trial も混ざるが、絞り込みの kind は validate / build だけ（C1）。 */
  listDraftReports(
    workspaceId: string,
    appId: string,
    query: { kind?: ReviewReportFilterKind; limit?: number; cursor?: string } = {},
  ): Promise<ReviewReportPage> {
    return this.json({ method: 'GET', path: `${appPath(workspaceId, appId)}/draft/reports`, params: { ...query } });
  }

  getDraftReport(workspaceId: string, appId: string, reportId: string): Promise<ReviewReport> {
    return this.json({ method: 'GET', path: `${appPath(workspaceId, appId)}/draft/reports/${enc(reportId)}` });
  }

  /**
   * 段階を ±1 動かす（D12）。If-Match は head の Revision id（無ければ 428、進んでいれば 409 revision_conflict）。
   * 進む条件が足りなければ 409 stage_incomplete、戻るのに理由が無ければ 422 invalid_stage。冪等キーは取らない。
   */
  setDraftStage(
    workspaceId: string,
    appId: string,
    body: { stage: number; reason?: string },
    headRevisionId: string,
  ): Promise<DraftMutation> {
    return this.json({
      method: 'PATCH',
      path: `${appPath(workspaceId, appId)}/draft`,
      json: body,
      headers: { 'If-Match': `"${headRevisionId}"` },
    });
  }

  // ---- 試運転と公開（studio。計画の S5） --------------------------------

  /**
   * 反映済みの版（head）を ZIP にして現行パッケージを置き換える（「試す」＝置換。要件 D1）。
   * 202 は受付で、配備の進み具合は getTrial で追う（G5）。公開中の app は confirmReplace が要る。
   */
  deployDraft(
    workspaceId: string,
    appId: string,
    body: { confirmReplace?: boolean },
    options: { idempotencyKey: string },
  ): Promise<DraftDeployment> {
    return this.json({
      method: 'POST',
      path: `${appPath(workspaceId, appId)}/draft:deploy`,
      json: body,
      headers: { 'Idempotency-Key': options.idempotencyKey },
    });
  }

  /** 配備した内容で試運転の run を開始する。配備が ready でなければ 409 trial_not_ready。 */
  startTrial(
    workspaceId: string,
    appId: string,
    trialId: string,
    body: { processKey: string; input?: JsonObject },
    options: { idempotencyKey: string },
  ): Promise<TrialStart> {
    return this.json({
      method: 'POST',
      path: `${appPath(workspaceId, appId)}/draft/trials/${enc(trialId)}:start`,
      json: body,
      headers: { 'Idempotency-Key': options.idempotencyKey },
    });
  }

  /** 新しい順。読み取りのときに終わった試運転の outcome が確定する（G6）。 */
  listTrials(workspaceId: string, appId: string, query: PageQuery = {}): Promise<StudioTrialPage> {
    return this.json({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/draft/trials`,
      params: { ...query },
    });
  }

  getTrial(
    workspaceId: string,
    appId: string,
    trialId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<StudioTrial> {
    return this.json({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/draft/trials/${enc(trialId)}`,
      signal: options.signal,
    });
  }

  /**
   * その版を「利用者に使ってよい」と印を付ける（設計 01 章）。配備は「試す」で済んでいる前提で、
   * 配備していない版は 409 not_deployed。段階は条件にしない（G26）。
   */
  publishApp(
    workspaceId: string,
    appId: string,
    body: { revisionId: string; reportId?: string; note?: string },
    options: { idempotencyKey: string },
  ): Promise<Publication> {
    return this.json({
      method: 'POST',
      path: `${appPath(workspaceId, appId)}:publish`,
      json: body,
      headers: { 'Idempotency-Key': options.idempotencyKey },
    });
  }

  /** 新しい順。Draft を捨てても公開の記録は残る（G1）。 */
  listPublications(workspaceId: string, appId: string, query: PageQuery = {}): Promise<PublicationPage> {
    return this.json({
      method: 'GET',
      path: `${appPath(workspaceId, appId)}/publications`,
      params: { ...query },
    });
  }

  // ---- SSE -------------------------------------------------------------

  /** イベント購読のstreamを開く。再接続・重複排除はEventStreamが担当する。 */
  openEvents(
    workspaceId: string,
    params: { appId?: string; runId?: string; requestId?: string; cursor?: string },
    signal?: AbortSignal,
  ) {
    return this.transport.openStream({
      method: 'GET',
      path: `/v1/workspaces/${enc(workspaceId)}/events`,
      params: { ...params },
      signal,
    });
  }

  /**
   * studio のイベント（W/studio/events）。cursor は W/events のものと互いに使い回せない。
   * sessionId / appId はサーバーで絞る（C26）。指定しなければ Workspace 全体。
   */
  openStudioEvents(
    workspaceId: string,
    params: { cursor?: string; sessionId?: string; appId?: string },
    signal?: AbortSignal,
  ) {
    return this.transport.openStream({
      method: 'GET',
      path: `${workspacePath(workspaceId)}/studio/events`,
      params: { ...params },
      signal,
    });
  }

  // ---- 内部 -------------------------------------------------------------

  private send(request: TransportRequest): Promise<TransportResponse> {
    return this.transport.send(request);
  }

  private async json<T>(request: TransportRequest): Promise<T> {
    const response = await this.send(request);
    return this.decode<T>(response, request.path);
  }

  private async decode<T>(response: TransportResponse, path: string): Promise<T> {
    if (response.status >= 400) throw await this.toError(response, path);
    const text = await response.text();
    if (text === '') return undefined as T;
    return JSON.parse(text) as T;
  }

  private async toError(response: TransportResponse, path: string): Promise<ApiError> {
    const text = await response.text().catch(() => '');
    let problem: Problem | null = null;
    try {
      const parsed = JSON.parse(text) as Partial<Problem>;
      if (parsed && typeof parsed.status === 'number' && typeof parsed.code === 'string') {
        problem = parsed as Problem;
      }
    } catch {
      problem = null;
    }
    return new ApiError(
      problem ?? {
        // problem+json でない応答（proxy障害など）も同じ型で扱い、握りつぶさない。
        type: 'about:blank',
        title: 'APIが想定外の応答を返しました',
        status: response.status,
        code: 'unexpected_response',
        detail: text.slice(0, 500),
        traceId: '',
      },
      path,
    );
  }
}

function workspacePath(workspaceId: string): string {
  return `/v1/workspaces/${enc(workspaceId)}`;
}

function sessionPath(workspaceId: string, sessionId: string): string {
  return `${workspacePath(workspaceId)}/sessions/${enc(sessionId)}`;
}

function appPath(workspaceId: string, appId: string): string {
  return `/v1/workspaces/${enc(workspaceId)}/microapps/${enc(appId)}`;
}

/**
 * Draft のファイルの URL。区切りの / は残し、段ごとに encode する。
 * API の {path:path} は decode 後のパスで照合するので、%2F にすると別のパスになる。
 */
function draftFilePath(workspaceId: string, appId: string, path: string): string {
  return `${appPath(workspaceId, appId)}/draft/files/${path.split('/').map(enc).join('/')}`;
}

/** ETag の前後の引用符を外す。 */
function unquoteEtag(value: string | null): string | null {
  if (value === null) return null;
  return value.trim().replace(/^"(.*)"$/, '$1');
}

/** Content-Disposition の filename*（RFC 5987 の UTF-8''…）を読む。無いか壊れていれば null。 */
function attachmentFileName(value: string | null): string | null {
  const encoded = value === null ? undefined : /filename\*=UTF-8''([^;]+)/i.exec(value)?.[1];
  if (!encoded) return null;
  try {
    return decodeURIComponent(encoded.trim());
  } catch {
    return null;
  }
}

function enc(segment: string): string {
  return encodeURIComponent(segment);
}

function ifMatchHeader(ifMatch?: string): Record<string, string> {
  return ifMatch ? { 'If-Match': ifMatch } : {};
}
